import "../../../assets/css/PremiumTools.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  RefreshCw,
  Zap,
  PenTool,
  AlignLeft,
  Settings,
  Sparkles,
  CheckCircle,
  AlertCircle,
  FileText,
  Sliders,
  ArrowLeft
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init"; 

const ParagraphGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT (Logic Preserved)
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [writingStyle, setWritingStyle] = useState("Informative");
  const [tone, setTone] = useState("Formal");
  const [length, setLength] = useState("Medium");
  const [vocabulary, setVocabulary] = useState("Standard");
  const [numParagraphs, setNumParagraphs] = useState(1);

  const [generatedText, setGeneratedText] = useState("");

  // Configuration Arrays
  const styles = ["Informative", "Creative", "Professional", "Academic", "Marketing"];
  const tones = ["Formal", "Casual", "Friendly", "Persuasive", "Empathetic", "Authoritative"];
  const lengths = ["Short (50-80 words)", "Medium (100-150 words)", "Long (200+ words)"];
  const vocabOptions = ["Simple", "Standard", "Advanced/Technical"];

  // ==========================================
  // 2. BACKEND LOGIC (UNCHANGED)
  // ==========================================
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error(err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          setCredits(userResponse.data[0].currentBalance);
        }
      } catch (err) {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. GENERATION LOGIC
  // ==========================================
  const constructPrompt = () => {
    return `You are an expert copywriter. Write content based on:
    Topic: ${topic}
    ${keywords ? `Keywords: ${keywords}` : ""}
    
    Settings:
    - Format: ${numParagraphs} Paragraph(s)
    - Style: ${writingStyle}
    - Tone: ${tone}
    - Length: ${length}
    - Vocabulary: ${vocabulary}

    Rules:
    1. Make it cohesive and clear.
    2. No intro/outro conversational filler.
    3. Return ONLY body text.
    4. Use proper grammar and punctuation.
    Provide the output in plain text format.`;
  };

  const generateParagraphs = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic.");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits.");
      return;
    }

    setGenerating(true);
    setError("");
    setGeneratedText("");

    try {
      // Deduct
      const deductResponse = await axios.post(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/deduct-credit`,
        { userEmail: user.email, amount: 1 }
      );
      if (!deductResponse.data.success) throw new Error("Failed to deduct credits");
      setCredits((prev) => prev - 1);

      // Generate
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${apiKey}`;
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 2048 },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (textResult) {
        setGeneratedText(textResult);
        toast.success("Content generated!");
      } else {
        throw new Error("Empty response");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message);
      setError(err.message);
      // Refund
      try {
        await axios.post(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/refund-credit`, {
          userEmail: user.email, amount: 1,
        });
        setCredits((prev) => prev + 1);
      } catch (refundErr) { console.error(refundErr); }
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedText).then(() => toast.success("Copied!"));
  };

  const downloadText = () => {
    const element = document.createElement("a");
    const file = new Blob([generatedText], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `paragraph-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
  };

  // ==========================================
  // 4. RENDER UI
  // ==========================================

  // Loading State
  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-wrapper">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3 text-muted fw-medium">Loading Paragraph Tool...</p>
        
      </div>
    );
  }

  // Auth Guard
  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card text-center">
          <div className="mb-4 icon-box mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{width: 60, height: 60}}>
            <PenTool size={30} />
          </div>
          <h2 className="fw-bold text-dark mb-2">Paragraph Writer</h2>
          <p className="text-muted mb-4">Log in to generate professional content with AI.</p>
          <button className="btn btn-primary btn-lg w-100 fw-semibold" onClick={() => window.location.href = "/login"}>
            Sign In to Continue
          </button>
        </div>
        
      </div>
    );
  }

  // Main UI
  return (
    <div className="premium-app-container">
      {/* Background Orbs */}
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      {/* Main Content */}
      <div className="container py-5 position-relative z-1">
        
        {/* Header Row */}
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-light rounded-circle p-2 back-btn d-flex align-items-center justify-content-center" onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </button>
            <h1 className="fw-bold m-0 text-white">
              Paragraph <span className="text-neon">Writer</span>
            </h1>
          </div>

          <div className="credits-badge">
            <Zap size={16} fill="#eab308" color="#eab308" />
            <span>{credits} Credits</span>
            {credits < 5 && (
              <button className="btn-buy-credits" onClick={() => window.location.href = "/user/select-package"}>
                Buy More
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger glass-alert">{error}</div>}

        <div className="row g-4">
          {/* LEFT SIDEBAR - FORM */}
          <div className="col-lg-4">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <Sliders size={18} className="text-neon" />
                  Settings
                </h5>
              </div>
              <div className="card-body-glass flex-grow-1 d-flex flex-column overflow-auto">
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Topic & Context *</label>
                  <textarea 
                    className="form-control input-group-glass" 
                    rows="3" 
                    placeholder="Describe what you want to write about..."
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    style={{resize: 'none'}}
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Keywords (Optional)</label>
                  <div className="position-relative">
                    <span className="position-absolute top-50 translate-middle-y text-light opacity-50" style={{left: '12px'}}><Sparkles size={16} /></span>
                    <input 
                      type="text"
                      className="form-control input-group-glass ps-5"
                      placeholder="e.g. organic, sustainable"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                    />
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-6">
                    <label className="form-label text-light opacity-75">Style</label>
                    <select className="form-select input-group-glass" value={writingStyle} onChange={(e) => setWritingStyle(e.target.value)}>
                      {styles.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label text-light opacity-75">Tone</label>
                    <select className="form-select input-group-glass" value={tone} onChange={(e) => setTone(e.target.value)}>
                      {tones.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                     <label className="form-label text-light opacity-75">Length</label>
                     <select className="form-select input-group-glass" value={length} onChange={(e) => setLength(e.target.value)}>
                      {lengths.map(l => <option key={l} value={l}>{l.split(' ')[0]}</option>)}
                     </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label text-light opacity-75">Vocabulary</label>
                    <select className="form-select input-group-glass" value={vocabulary} onChange={(e) => setVocabulary(e.target.value)}>
                      {vocabOptions.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label text-light opacity-75 mb-0">Paragraphs</label>
                    <span className="badge bg-dark border border-secondary">{numParagraphs}</span>
                  </div>
                  <input 
                    type="range" 
                    className="form-range" 
                    min="1" 
                    max="5" 
                    step="1" 
                    value={numParagraphs}
                    onChange={(e) => setNumParagraphs(e.target.value)}
                  />
                  <div className="d-flex justify-content-between text-light opacity-50 small">
                    <span>1</span><span>5</span>
                  </div>
                </div>

                <div className="mt-auto pt-3">
                  <button 
                    className="btn btn-neon-primary w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={generateParagraphs}
                    disabled={generating || credits < 1 || !topic.trim()}
                  >
                    {generating ? (
                      <><RefreshCw className="spin" size={18} /> Generating...</>
                    ) : (
                      <><Zap size={18} /> Generate (1 Credit)</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR - OUTPUT */}
          <div className="col-lg-8">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass d-flex justify-content-between align-items-center">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <FileText size={18} className={generatedText ? "text-success" : "text-neon"} />
                  Result
                </h5>
                <div className="d-flex align-items-center gap-2">
                  {generatedText && (
                    <>
                      <span className="badge bg-dark border border-secondary">{generatedText.split(' ').length} words</span>
                      <button className="btn btn-sm btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center" onClick={copyToClipboard} title="Copy">
                        <Copy size={14} />
                      </button>
                      <button className="btn btn-sm btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center" onClick={downloadText} title="Download">
                        <Download size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="card-body-glass flex-grow-1 overflow-auto position-relative" style={{ minHeight: "500px" }}>
                {generating ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-75 py-5">
                    <div className="spinner-border text-neon mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-white">Writing your content...</h5>
                    <p className="text-light opacity-75">Optimizing for {tone} tone and {writingStyle} style.</p>
                  </div>
                ) : generatedText ? (
                  <div className="text-white" style={{ fontFamily: 'Georgia, serif', fontSize: '1.05rem', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
                    {generatedText.split('\n').map((para, idx) => (
                      para.trim() && <p key={idx} className="mb-4">{para}</p>
                    ))}
                  </div>
                ) : (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-25 py-5">
                    <AlignLeft size={64} className="mb-3" />
                    <h5 className="text-white opacity-50">Your content will appear here</h5>
                    <p className="text-center">Configure settings on the left to start writing.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* --- INLINE CSS FOR MODERN STYLING --- */}
      
    </div>
  );
};

export default ParagraphGenerator;